import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Cache for user data to avoid repeated Firestore calls
const userCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

export const getUsernameById = async (userId: string): Promise<string | null> => {
  if (!userId) return null;
  
  // Check cache first
  const cached = userCache.get(userId);
  if (cached && isCacheValid(cached.timestamp)) {
    const data = cached.data;
    return data?.name || data?.username || data?.displayName || null;
  }
  
  try {
    const userRef = doc(db, 'Users', userId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      userCache.set(userId, { data: null, timestamp: Date.now() });
      return null;
    }

    const data = snap.data() as any;
    // Cache the result
    userCache.set(userId, { data, timestamp: Date.now() });
    
    return data?.name || data?.username || data?.displayName || null;
  } catch (error) {
    console.error('Error fetching username by id:', error);
    return null;
  }
};

export const getUserById = async <T = any>(userId: string): Promise<T | null> => {
  if (!userId) return null;
  
  // Check cache first
  const cached = userCache.get(userId);
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data as T;
  }
  
  try {
    const userRef = doc(db, 'Users', userId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      userCache.set(userId, { data: null, timestamp: Date.now() });
      return null;
    }
    
    const data = snap.data() as T;
    // Cache the result
    userCache.set(userId, { data, timestamp: Date.now() });
    
    return data;
  } catch (error) {
    console.error('Error fetching user by id:', error);
    return null;
  }
};

// Utility function to clear cache (useful for logout or when user data changes)
export const clearUserCache = (userId?: string): void => {
  if (userId) {
    userCache.delete(userId);
  } else {
    userCache.clear();
  }
};